'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { IconArrowUp, IconBriefcase, IconCircleCheck, IconUser } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SubscriptionPackageResponseDto } from '@/generated';
import { useUpdateGlobalUser } from '@/hooks/useUpdateGlobalUser';
import { formatDateToDay } from '@/utils/formatDayOrTime';

type Props = {
  subscription: SubscriptionPackageResponseDto;
  hasPendingProduct?: boolean;
  isTrial?: boolean;
  onClose: () => void;
};

export const AfterSubscriptionPaidScreen: React.FC<Props> = ({ subscription, hasPendingProduct, isTrial, onClose }) => {
  const t = useTranslations('subscription');
  const reloadUser = useUpdateGlobalUser();

  useEffect(() => {
    reloadUser();
  }, [reloadUser]);

  const remainingListings = subscription.balances?.listings.remaining;
  const totalListings = subscription.balances?.listings.total;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <div className='flex flex-col items-center gap-[16px]'>
          <div className='size-[72px] rounded-full bg-primary-tint/15 flex items-center justify-center shrink-0'>
            <IconCircleCheck size={36} className='text-primary-green-day' />
          </div>

          <DialogHeader className='items-center text-center sm:text-center'>
            <DialogTitle className='text-[28px] leading-[34px]'>
              {t(isTrial ? 'purchase_trial_title' : 'purchase_thank_you_title')}
            </DialogTitle>
            <DialogDescription className='text-[15px] text-text-primary-day'>
              {totalListings != null
                ? t(isTrial ? 'purchase_trial_subtitle' : 'purchase_thank_you_subtitle', {
                    count: totalListings,
                    period: t('purchase_thank_you_period_until', { date: formatDateToDay(subscription.expiresAt) }),
                  })
                : subscription.plan.name}
            </DialogDescription>
            {hasPendingProduct && (
              <p className='text-[15px] text-text-disabled-day!'>{t('purchase_thank_you_product_note')}</p>
            )}
          </DialogHeader>

          {remainingListings != null && (
            <div className='w-full flex flex-col gap-1 p-4 rounded-2xl bg-primary-tint/10'>
              <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-2 min-w-0'>
                  {isTrial ? (
                    <Image src='/assets/icons/icon-set.svg' width={40} height={40} alt='' className='shrink-0' />
                  ) : (
                    <IconBriefcase size={22} className='text-primary-green-day shrink-0' />
                  )}
                  <span className='text-[14px] text-text-primary-day'>{t('purchase_thank_you_remaining_label')}</span>
                </div>
                <span className='text-[22px] font-bold text-primary-green-day shrink-0'>{remainingListings}</span>
              </div>

              {hasPendingProduct && (
                <p className='text-[13px] text-text-disabled-day!'>{t('purchase_thank_you_remaining_note')}</p>
              )}
            </div>
          )}

          <div className='w-full flex flex-col gap-2'>
            <div className='flex items-center gap-3 p-3 rounded-xl bg-bg2-day'>
              <div className='size-[36px] rounded-full bg-bg-day flex items-center justify-center shrink-0'>
                <IconUser size={18} className='text-primary-green-day' />
              </div>
              <span className='text-[13px] text-text-primary-day'>{t('purchase_thank_you_info_profile')}</span>
            </div>

            <div className='flex items-center gap-3 p-3 rounded-xl bg-bg2-day'>
              <div className='size-[36px] rounded-full bg-bg-day flex items-center justify-center shrink-0'>
                <IconArrowUp size={18} className='text-primary-green-day' />
              </div>
              <span className='text-[13px] text-text-primary-day'>{t('purchase_thank_you_info_promo')}</span>
            </div>
          </div>

          <Button
            variant='default'
            onClick={onClose}
            className='w-full h-auto rounded-[12px] py-[10px] text-bg-day mt-[4px] transition-transform duration-100 active:scale-[0.97]'
          >
            {t('purchase_thank_you_ok')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
